import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './CourseCalendar.scss';
import Card from '../Card';
import { Button, Calendar, Badge, Modal, Table } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

const className = 'c-CourseCalendar';

const getBadgeStyle = (
  students,
  totalSeats,
  startDateStr,
  courseOfferStatus
) => {
  const perc = (100 * students) / totalSeats;

  const [red, green, yellow, grey] = [
    '#FF4D4F',
    '#81D25C',
    '#F4BE53',
    '#F0f0f0',
  ];

  const startDate = new Date(startDateStr);
  const today = new Date();
  const isCanceledOrInactive =
    courseOfferStatus === 'Inactive' || courseOfferStatus === 'Cancelled';
  if (startDate < today || isCanceledOrInactive) {
    return { backgroundColor: grey, color: '#999' };
  }

  // If class filled is :-
  // lt 50%, then show red bg.
  if (perc < 50) return { backgroundColor: red };
  // for 50%-100% show yellow bg.
  if (perc < 100) return { backgroundColor: yellow };
  // for gt 100% show green bg.
  return { backgroundColor: green };
};

const getTiming = (localStartDate, localEndDate) => {
  const startDate = new Date(localStartDate);
  const endDate = new Date(localEndDate);

  const startTime = startDate
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase();

  const endTime = endDate
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase();

  const timeRange = `${startTime} - ${endTime}`;
  return timeRange;
};

const getModalTableData = (momentDt, calData, forAdmin) => {
  const tblCols = [
    {
      title: 'Course Code',
      dataIndex: 'courseData',
      key: 'courseData',
      render: ({ courseCode, wave, timing, courseOfferId }) => {
        if (forAdmin)
          return (
            <Link to={`/course-offer/${courseOfferId}`}>
              <p style={{ margin: 0 }}>
                {courseCode} {wave ? `Wave ${wave}` : ''}
              </p>
              <p className={`${className}__course-timing`}>{timing}</p>
            </Link>
          );
        else
          return (
            <div>
              <p style={{ margin: 0 }}>
                {courseCode} {wave ? `Wave ${wave}` : ''}
              </p>
              <p className={`${className}__course-timing`}>{timing}</p>
            </div>
          );
      },
    },
    {
      title: 'Course Name',
      dataIndex: 'courseName',
      key: 'courseName',
    },
    {
      title: 'Status',
      dataIndex: 'courseOfferStatus',
      key: 'courseOfferStatus',
      render: (status, record) => {
        if (status === 'Cancelled') {
          return <p className={`${className}__course-timing`}>{status}</p>;
        } else if (record.isCompleted) {
          return <p className={`${className}__course-timing`}>Completed</p>;
        }
        return <p className={`${className}__course-timing`}>{status}</p>;
      },
    },
    {
      title: 'Students',
      dataIndex: 'classStrength',
      key: 'classStrength',
      render: ({ students, seats }, record) => {
        const badgeContent = `${students}/${seats}`;
        const { courseOfferStatus, localStartDate } = record;
        const badgeStyle = getBadgeStyle(
          students,
          seats,
          localStartDate,
          courseOfferStatus
        );
        return <Badge count={badgeContent} style={badgeStyle} />;
      },
    },
  ];

  const [year, month, day] = [
    momentDt.year(),
    momentDt.month(),
    momentDt.date(),
  ];

  const dateCourseData = calData?.[year]?.[month]?.[day] || [];
  const tblData = dateCourseData.map((course) => {
    const {
      courseCode,
      wave,
      courseDescription,
      studentCount,
      maxRegistrations,
      localStartDate,
      localEndDate,
      courseOfferId,
      courseOfferStatus,
    } = course;
    let isCompleted = false;
    const startDate = new Date(localStartDate);
    const today = new Date();
    if (startDate < today) {
      isCompleted = true;
    }
    return {
      courseData: {
        courseCode,
        wave,
        timing: getTiming(localStartDate, localEndDate),
        courseOfferId,
      },
      courseName: courseDescription,
      classStrength: {
        students: studentCount,
        seats: maxRegistrations,
      },
      courseOfferStatus,
      isCompleted,
      localStartDate,
    };
  });
  return { tblCols, tblData };
};

const CourseCalendar = ({ title, calData, onCalChange, courses, forAdmin }) => {
  // date for which the modal has to show data
  const [modalDateDetails, setModalDateDetails] = useState(null);
  const [showLegendModal, setShowLegendModal] = useState(false);

  const showDayDetailsModal = !!modalDateDetails;

  const openDayDetailsModal = (moment_dt) => setModalDateDetails(moment_dt);
  const closeDayDetailsModal = () => setModalDateDetails(null);

  const openLegendModal = () => setShowLegendModal(true);
  const closeLegendModal = () => setShowLegendModal(false);

  const renderDateCell = (value) => {
    const yr = value.year();
    const month = value.month();
    const day = value.date();
    const coursesList = calData?.[yr]?.[month]?.[day] || [];

    const handleCellDataClick = (e) => {
      e.stopPropagation();
      openDayDetailsModal(value);
    };

    const reducedList = coursesList.slice(0, 2);
    const moreCount = coursesList?.length - reducedList?.length;

    return (
      <div onClick={handleCellDataClick}>
        {reducedList.map((course, key) => {
          const {
            courseCode,
            studentCount,
            maxRegistrations,
            startDate,
            courseOfferStatus,
          } = course;
          const badgeContent = `${courseCode} (${studentCount}/${maxRegistrations})`;
          const badgeStyle = getBadgeStyle(
            studentCount,
            maxRegistrations,
            startDate,
            courseOfferStatus
          );
          return <Badge count={badgeContent} style={badgeStyle} />;
        })}
        {moreCount > 0 && (
          <div className={`${className}__load__more`}>+{moreCount}</div>
        )}
      </div>
    );
  };

  const renderDayDetailsModalContent = () => {
    if (!modalDateDetails) return null;
    const [year, month, day] = [
      modalDateDetails.year(),
      modalDateDetails.month(),
      modalDateDetails.date(),
    ];
    const titleDate = modalDateDetails.format('DD MMM YYYY (dddd)');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzOffset = modalDateDetails.format('Z z');
    const timeZone = `${tz} ${tzOffset}`;

    const { tblCols, tblData } = getModalTableData(
      modalDateDetails,
      calData,
      forAdmin
    );
    return (
      <div
        className={`${className}__modal-inner ${className}__day-details-modal-inner`}
      >
        <div className={`${className}__modal-title-container`}>
          <p className={`${className}__modal-title`}>{titleDate}</p>
          <p className={`${className}__day-details-tz`}>{timeZone}</p>
        </div>
        <div>
          <Table columns={tblCols} dataSource={tblData} pagination={false} />
        </div>
      </div>
    );
  };

  const renderLegendModalContent = () => {
    const tblCols = [
      {
        title: 'Course Code',
        dataIndex: 'value',
        key: 'value',
      },
      {
        title: 'Course Name',
        dataIndex: 'label',
        key: 'label',
      },
    ];

    return (
      <div className={`${className}__modal-inner`}>
        <Table columns={tblCols} dataSource={courses} pagination={false} />
      </div>
    );
  };

  const renderColorLegend = () => {
    const colorMap = [
      {
        color: '#81D25C',
        label: 'Active and fully filled',
      },
      {
        color: '#F4BE53',
        label: 'Active and >=50% filled',
      },
      {
        color: '#FF4D4F',
        label: 'Active and <50% filled',
      },
      {
        color: '#F0f0f0',
        label: 'Inactive/Cancelled/Completed',
      },
    ];

    const tblCols = [
      {
        title: 'Color',
        dataIndex: 'color',
        key: 'color',
        render: (color) => {
          return (
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: color,
                borderRadius: '100%',
              }}
            />
          );
        },
      },
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
      },
    ];

    return (
      <div className={`${className}__modal-inner`}>
        <div className={`${className}__modal-title-container`}>
          <p className={`${className}__modal-title`}>Legend</p>
        </div>
        <div>
          <Table columns={tblCols} dataSource={colorMap} pagination={false} />
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <Card>
        <div className={`${className}__flex-container`}>
          <div className={`${className}__title`}>{title}</div>
          <div className={`${className}__legend`}>
            {courses.length > 0 && (
              <Button
                ghost
                type="primary"
                icon={<InfoCircleOutlined />}
                onClick={openLegendModal}
              >
                Legend
              </Button>
            )}
          </div>
        </div>
        <div className={`${className}__cal-container`}>
          <Calendar dateCellRender={renderDateCell} onChange={onCalChange} />
        </div>
      </Card>

      {/* Day Details Modal */}
      <Modal
        visible={showDayDetailsModal}
        onCancel={closeDayDetailsModal}
        footer={null}
      >
        {renderDayDetailsModalContent()}
      </Modal>

      {/* Legend Modal */}
      {courses.length > 0 && (
        <Modal
          visible={showLegendModal}
          onCancel={closeLegendModal}
          footer={null}
        >
          {renderColorLegend()}
          {renderLegendModalContent()}
        </Modal>
      )}
    </div>
  );
};

CourseCalendar.defaultProps = {
  forAdmin: false,
};

CourseCalendar.propTypes = {
  forAdmin: PropTypes.bool,
};

export default CourseCalendar;
